import {MapTo, Container} from '@adobe/aem-react-editable-components';
import {AEM_MAPPED_TYPES_LIST, AEM_IGNORE_TYPES_LIST} from '@/constants';

const NullComponent = () => null;

const autoMappedTypes = new Set();

/**
 * Ensures all resource types in cqItems have a component mapping.
 * Unmapped types are automatically mapped to a null component to prevent
 * "Component not mapped for resourcetype" console errors.
 *
 * Call this before rendering a Page component.
 *
 * @param {Object} cqItems - The cqItems object from the model
 * @param {string[]} [cqItemsOrder] - Optional cqItemsOrder to limit scope
 */
export const mapUnknownComponents = (cqItems, cqItemsOrder) => {
  const keys = cqItemsOrder || Object.keys(cqItems);

  for (const key of keys) {
    const item = cqItems[key];
    if (!item) continue;

    const resourceType = item[':type'];
    if (!resourceType || autoMappedTypes.has(resourceType)) continue;

    if (!AEM_MAPPED_TYPES_LIST.includes(resourceType) && !AEM_IGNORE_TYPES_LIST.includes(resourceType)) {
      MapTo(resourceType)(item[':items'] ? Container : NullComponent);
    }
    autoMappedTypes.add(resourceType);

    if (item[':items']) {
      mapUnknownComponents(item[':items'], item[':itemsOrder']);
    }
  }
};

/**
 * Filters cqItems to only include items matching a given resourceType, searching recursively
 * through nested ':items'.
 *
 * @param {Object} cqItems - The cqItems object from the model
 * @param {string[]} cqItemsOrder - The cqItemsOrder array from the model
 * @param {string} resourceType - The AEM resource type to filter by
 * @returns {{ filteredItems: Object, filteredItemsOrder: string[] }}
 */
export const filterCqItemsByType = (cqItems, cqItemsOrder, resourceType) => {
  const filteredItems = {};
  const filteredItemsOrder = [];

  const traverse = (items, itemsOrder) => {
    const keys = itemsOrder || Object.keys(items);
    for (const key of keys) {
      const item = items[key];
      if (!item) continue;

      if (item[':type'] === resourceType) {
        filteredItems[key] = item;
        filteredItemsOrder.push(key);
      }

      if (item[':items']) {
        traverse(item[':items'], item[':itemsOrder']);
      }
    }
  };

  traverse(cqItems, cqItemsOrder);
  return {filteredItems, filteredItemsOrder};
};
